import React from "react";
import getTitle from "../../../helpers/getTitle";
import useTranslate from "../../../modules/local/useTranslate";
import {formatSimplifyDate, isSameDay} from "../../../helpers/dateUtils";
import _ from 'lodash'
import {Image} from "antd";
import {ImagePlaceholder} from "../../../conts/imageBase64";
import {AnnouncementTypes} from "../../AppLocals/Organization/consts/AnnouncementTypes";
import {BsCalendar2Date, BsCalendarCheck, BsCalendarX} from "react-icons/bs";

// Per-type color config — inline styles only, no Tailwind color classes
// so no purge/JIT issues
export const TYPE_COLORS = {
    WEDDING: {
        bg: '#FFF0F3',
        iconBg: '#F43F5E',
        accent: '#E11D48',
        label: '#BE123C',
        timeBg: '#FFE4E8',
        timeText: '#9F1239',
        border: '#FECDD3',
    },
    BANNS_OF_MARRIAGE: {
        bg: '#FFF0F6',
        iconBg: '#EC4899',
        accent: '#DB2777',
        label: '#9D174D',
        timeBg: '#FCE7F3',
        timeText: '#831843',
        border: '#FBCFE8',
    },
    BAPTISM: {
        bg: '#EFF6FF',
        iconBg: '#3B82F6',
        accent: '#2563EB',
        label: '#1D4ED8',
        timeBg: '#DBEAFE',
        timeText: '#1E40AF',
        border: '#BFDBFE',
    },
    OBITUARY: {
        bg: '#F8FAFC',
        iconBg: '#475569',
        accent: '#334155',
        label: '#1E293B',
        timeBg: '#F1F5F9',
        timeText: '#334155',
        border: '#CBD5E1',
    },
    FINANCIAL_AID: {
        bg: '#ECFDF5',
        iconBg: '#10B981',
        accent: '#059669',
        label: '#065F46',
        timeBg: '#D1FAE5',
        timeText: '#064E3B',
        border: '#A7F3D0',
    },
    PATRON_FEAST: {
        bg: '#FFFBEB',
        iconBg: '#F59E0B',
        accent: '#D97706',
        label: '#92400E',
        timeBg: '#FEF3C7',
        timeText: '#78350F',
        border: '#FDE68A',
    },
    ANNOUNCEMENT: {
        bg: '#EFF6FF',
        iconBg: '#6366F1',
        accent: '#4F46E5',
        label: '#3730A3',
        timeBg: '#E0E7FF',
        timeText: '#312E81',
        border: '#C7D2FE',
    },
    DEFAULT: {
        bg: '#F8FAFC',
        iconBg: '#94A3B8',
        accent: '#64748B',
        label: '#475569',
        timeBg: '#F1F5F9',
        timeText: '#334155',
        border: '#E2E8F0',
    },
}

export const OrganizationAnnouncementItem = ({item}) => {

    const t = useTranslate()
    const {
        title, type_label, type_code, photo,
        content, group,
        time_close, time_open, shared_url,
    } = item ?? {}

    const colors = TYPE_COLORS[type_code] || TYPE_COLORS.DEFAULT
    const typeIcon = _.find(AnnouncementTypes, {value: type_code || item?.type})?.icon

    const TagBlock = ({overlay = false}) => (
        <div
            className="flex items-center gap-2"
            style={overlay ? {
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(6px)',
                borderRadius: 14,
                padding: '6px 10px 6px 6px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.13)',
            } : {}}>
            {/* Icon circle */}
            <div style={{
                width: 28,
                height: 28,
                borderRadius: 12,
                background: colors.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 16,
            }}>
                <span style={{filter: 'brightness(0) invert(1)', display: 'flex', alignItems: 'center'}}>
                    {typeIcon}
                </span>
            </div>
            <div className="flex flex-col">
                <span style={{ color: colors.label }} className="font-bold text-sm tracking-wide leading-snug">
                    {type_label}
                </span>
                {group && (
                    <div className="text-xs leading-snug">
                        <span className="font-semibold text-gray-700 tracking-wider">
                            {getTitle(group)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )

    const TimeRow = ({icon: Icon, label, value}) => value && (
        <div className="text-sm lg:text-base space-x-2 tracking-wide">
            <Icon style={{fontSize: 13, color: colors.timeText, flexShrink: 0}}/>
            {label && (
                <span className="text-gray-500 italic text-sm">
                    {label}
                </span>
            )}
            <span style={{ color: colors.timeText }} className="font-bold capitalize">
                {value}
            </span>
        </div>
    )

    const renderTimeSection = () => {
        const hasBoth = time_open && time_close
        const sameDay = hasBoth && isSameDay(time_open, time_close)

        let content
        if (hasBoth && sameDay) {
            content = (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px'}}>
                    <TimeRow
                        icon={BsCalendar2Date}
                        value={formatSimplifyDate(time_open, 'fullShort')}
                    />
                    <TimeRow
                        icon={BsCalendar2Date}
                        value={`${formatSimplifyDate(time_open, '', 'HH:mm')} → ${formatSimplifyDate(time_close, '', 'HH:mm')}`}
                    />
                </div>
            )
        } else if (hasBoth) {
            content = (
                <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                    <TimeRow icon={BsCalendarCheck} label={t('start')}
                             value={formatSimplifyDate(time_open, 'fullTime')}/>
                    <TimeRow icon={BsCalendarX} label={t('end')}
                             value={formatSimplifyDate(time_close, 'fullTime')}/>
                </div>
            )
        } else {
            const t0 = time_open || time_close
            const icon = time_open ? BsCalendarCheck : BsCalendarX
            const label = time_open ? t('start') : t('end')
            content = (
                <TimeRow icon={icon} label={label} value={formatSimplifyDate(t0, 'fullTime')}/>
            )
        }

        return (
            <div style={{
                // marginTop: 12,
                padding: '10px 12px',
                borderRadius: 10,
                background: colors.timeBg,
                // border: `1px solid ${colors.border}`,
            }}>
                {content}
            </div>
        )
    }

    return (
        <div
            className=""
            style={{
                overflow: 'hidden',
                border: `1px solid ${photo ? '#E2E8F0' : colors.border}`,
                background: photo ? '#fff' : colors.bg,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                borderRadius: '0.75rem',
            }}>
            {/* Photo with overlay tag */}
            {photo ? (
                <div style={{position: 'relative'}}>
                    <Image
                        src={photo}
                        fallback={ImagePlaceholder}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto'
                        }}
                        wrapperClassName="w-full background-200"
                        preview={false}
                    />
                    <div style={{position: 'absolute', top: 12, left: 12}}>
                        <TagBlock overlay/>
                    </div>
                </div>
            ) : (
                <div style={{padding: '0.75rem 0.75rem 0'}}>
                    <TagBlock/>
                </div>
            )}

            {/* Body */}
            <div className="p-3 flex flex-col gap-3">
                <div className="font-bold text-lg text-gray-800 tracking-wide">
                    {title}
                </div>
                {content && (
                    <div className="text-gray-700 text-base whitespace-pre-line">
                        {content}
                    </div>
                )}
                {shared_url && (
                    <a
                        href={shared_url} target="_blank" rel="noopener noreferrer"
                        className="flex-1 text-blue-600 italic no-underline max-lines-1">
                        {shared_url}
                    </a>
                )}

                {(time_open || time_close) && renderTimeSection()}
            </div>
        </div>
    )
}